import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown,
  Close,
  ContentCopy,
  Download,
  OpenInNew,
  PauseCircleOutline,
  PlayCircleOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tokens } from '../../theme/tokens';
import { ConfirmDialog, InlineMessage, StatusChip } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import {
  syncRunKindChipLabel,
  syncRunKindHint,
  syncRunKindLabel,
  syncRunKindTone,
  syncRunUpdatedNoun,
  type SyncRunKind,
} from '../../utils/syncRunKind';
import { fetchStoreSyncDetail, runStoreSync, setStoreSyncPaused, type StoreSyncRunResult } from './api';
import {
  errorClipboardText,
  formatAgo,
  formatDuration,
  formatWhen,
  frequencyLabel,
  nextRunText,
  platformLabel,
  presentStatus,
  runHistoryCsv,
  statusSubline,
  type StoreSyncRow,
} from './model';

type RunKind = 'pricing' | 'compare' | 'full';
const RUN_KINDS: RunKind[] = ['pricing', 'compare', 'full'];

/** Platforms whose admin run is a background job the drawer polls (MOT-436, off the request since MOT-524). */
const BACKGROUND_RUN_PLATFORMS = new Set(['lightspeed', 'lightspeed_ecom']);
/** The server's `ADMIN_RUN_BUDGET_MS`, in minutes, for the confirm copy. */
const RUN_BUDGET_MINUTES = 4;

interface RunOutcome {
  productsProcessed?: number | null;
  errorCount?: number | null;
  continues?: boolean;
}

function runOutcomeToast(kindLabel: string, store: string | undefined, result: RunOutcome): string {
  const processed = result.productsProcessed ?? 0;
  const errors = result.errorCount ?? 0;
  const counts = `${processed.toLocaleString()} product${processed === 1 ? '' : 's'}${errors > 0 ? `, ${errors} error${errors === 1 ? '' : 's'}` : ''}`;
  const tail = result.continues ? ' It stopped at its time limit; the next sync continues from there.' : '';
  return `${kindLabel} ran for ${store}: ${counts}.${tail}`;
}

const RESULT_TONE = { clean: 'positive', warnings: 'caution', failed: 'problem', running: 'info', cancelled: 'neutral' } as const;
const RESULT_TEXT = { clean: 'Clean', warnings: 'Warnings', failed: 'Failed', running: 'Running', cancelled: 'Cancelled' } as const;

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
} as const;

interface StoreSyncDrawerProps {
  /** The row the drawer was opened from; null closes it. */
  row: StoreSyncRow | null;
  onClose: () => void;
}

export const StoreSyncDrawer: React.FC<StoreSyncDrawerProps> = ({ row, onClose }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { startImpersonationWithOrg } = useImpersonation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmKind, setConfirmKind] = useState<RunKind | null>(null);
  // The background Lightspeed run this drawer started; its outcome is toasted once it lands.
  const [watchedRunId, setWatchedRunId] = useState<string | null>(null);

  const id = row?.integrationId ?? null;
  const detailQuery = useQuery({
    queryKey: ['admin-store-sync', 'detail', id],
    queryFn: () => fetchStoreSyncDetail(id as string),
    enabled: Boolean(id),
    // A run started here finishes in the background: keep reading until it does.
    refetchInterval: (query) => {
      const data = query.state.data;
      const jobLive = data?.adminRun?.status === 'queued' || data?.adminRun?.status === 'running';
      return data?.row.status === 'running' || jobLive || watchedRunId !== null ? 5000 : false;
    },
  });

  // The detail row is fresher than the table row it was opened from.
  const live = detailQuery.data?.row ?? row;
  const refreshLists = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-store-sync'] });
  };

  const run = useMutation({
    mutationFn: (kind: RunKind) => runStoreSync(id as string, kind),
    onSuccess: (result, kind) => {
      const store = live?.organizationName || live?.name;
      if (result.existing) {
        showToast('A sync is already queued or running for this store.', 'success');
      } else if (result.mode === 'inline') {
        showToast(runOutcomeToast(syncRunKindLabel(kind), store, result), (result.errorCount ?? 0) > 0 ? 'warning' : 'success');
      } else if (result.adminRunId) {
        setWatchedRunId(result.adminRunId);
        showToast(`${syncRunKindLabel(kind)} started for ${store}. It runs in the background; the result shows here.`, 'success');
      } else {
        showToast(`${syncRunKindLabel(kind)} queued for ${store}`, 'success');
      }
      refreshLists();
    },
    onError: (error: Error) => showToast(error.message || 'Could not start the sync.', 'error'),
  });

  const adminRun = detailQuery.data?.adminRun ?? null;
  useEffect(() => {
    if (!watchedRunId || !adminRun || adminRun.runId !== watchedRunId) return;
    if (adminRun.status !== 'done' && adminRun.status !== 'failed') return;
    const store = live?.organizationName || live?.name;
    const kindLabel = syncRunKindLabel(adminRun.kind);
    if (adminRun.status === 'failed') {
      showToast(`${kindLabel} for ${store} did not finish: ${adminRun.error || 'the run failed.'}`, 'error');
    } else if (adminRun.alreadyRunning) {
      showToast(`Another run was already writing to ${store}, so this one did nothing.`, 'warning');
    } else {
      showToast(runOutcomeToast(kindLabel, store, adminRun), (adminRun.errorCount ?? 0) > 0 ? 'warning' : 'success');
    }
    setWatchedRunId(null);
    queryClient.invalidateQueries({ queryKey: ['admin-store-sync'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminRun?.runId, adminRun?.status, watchedRunId]);

  // A run watched in one drawer is not carried to another store's drawer.
  useEffect(() => {
    setWatchedRunId(null);
  }, [id]);

  const pause = useMutation({
    mutationFn: (paused: boolean) => setStoreSyncPaused(id as string, paused),
    onSuccess: ({ paused }) => {
      showToast(paused ? 'Schedule paused. Manual runs still work.' : 'Schedule resumed.', 'success');
      refreshLists();
    },
    onError: (error: Error) => showToast(error.message || 'Could not change the schedule.', 'error'),
  });

  if (!row || !live) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const detail = detailQuery.data;
  const status = presentStatus(live.status);
  const scheduled = live.schedule.frequency !== 'manual';
  const jobLive = adminRun?.status === 'queued' || adminRun?.status === 'running';
  const isRunning = live.status === 'running' || run.isPending || jobLive;
  const lastKind: SyncRunKind = live.lastRun?.kind ?? 'catalog';
  const sub = statusSubline(live, syncRunKindLabel(lastKind));
  const next = nextRunText(live);
  const canImpersonate = Boolean(live.organizationId) && live.orgIssue === null;
  const blocked = live.runBlockedReason;
  const kindBlocked = (k: RunKind): string | null => blocked ?? live.runKindBlocked?.[k] ?? null;
  // Scheduled stores default to a pricing run, manual ones to compare; a platform without that
  // kind (Lightspeed eCom has no pricing run) falls back to the first kind it does have.
  const preferredKind: RunKind = scheduled ? 'pricing' : 'compare';
  const mainKind: RunKind =
    kindBlocked(preferredKind) === null ? preferredKind : RUN_KINDS.find((k) => kindBlocked(k) === null) ?? preferredKind;
  const mainBlocked = kindBlocked(mainKind);
  const runsInBackground = BACKGROUND_RUN_PLATFORMS.has(live.platform);
  const runningKindLabel = syncRunKindLabel(jobLive && adminRun ? adminRun.kind : lastKind === 'catalog' ? mainKind : lastKind).toLowerCase();

  const copyErrors = async () => {
    if (!detail) return;
    try {
      await navigator.clipboard.writeText(errorClipboardText(detail.errors));
      showToast('Error copied', 'success');
    } catch {
      showToast('Could not copy. Select the text in the error and copy it instead.', 'error');
    }
  };

  const exportCsv = () => {
    if (!detail) return;
    const blob = new Blob([runHistoryCsv(detail.runs, syncRunUpdatedNoun)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${live.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-sync-runs.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const facts: Array<{ label: string; value: string; tone?: 'problem' | 'caution' }> = [
    { label: 'Schedule', value: live.schedule.paused ? `${frequencyLabel(live.schedule.frequency)} · paused` : frequencyLabel(live.schedule.frequency) },
    { label: 'Next run', value: next.text },
    { label: 'Last success', value: live.lastSuccessAt ? formatWhen(live.lastSuccessAt) : '—' },
    {
      label: 'Updated',
      value:
        live.lastRun?.updated === null || live.lastRun?.updated === undefined
          ? '—'
          : `${live.lastRun.updated.toLocaleString()} ${syncRunUpdatedNoun(lastKind)}`,
    },
    { label: 'Errors', value: live.lastRun ? String(live.lastRun.errors) : '—', tone: live.lastRun && live.lastRun.errors > 0 ? 'problem' : undefined },
    { label: 'Warnings', value: live.lastRun ? String(live.lastRun.warnings) : '—', tone: live.lastRun && live.lastRun.warnings > 0 ? 'caution' : undefined },
  ];

  const note =
    live.status === 'no_details'
      ? status.help
      : live.status === 'never'
        ? 'This integration is connected but has never run. Run a sync to create the first listings.'
        : live.status === 'missed'
          ? `The ${live.schedule.frequency} run did not start. Check that the scheduler job exists for this integration, or run it now.`
          : null;

  return (
    <>
      <Drawer
        anchor="right"
        open
        onClose={onClose}
        PaperProps={{ sx: { width: 'min(580px, 100vw)', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ p: '18px 20px 14px', borderBottom: `1px solid ${tokens.line.default}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...sectionLabelSx, color: tokens.ink.muted }}>
                {platformLabel(live.platform)}
                {live.organizationName ? ` · ${live.organizationName}` : ''}
              </Typography>
              <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 700, mt: 0.25, overflowWrap: 'anywhere' }}>
                {live.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <StatusChip label={status.label} tone={status.tone} dot />
                {live.domain && (
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, overflowWrap: 'anywhere' }}>{live.domain}</Typography>
                )}
              </Box>
            </Box>
            <IconButton aria-label="Close" onClick={onClose} sx={{ alignSelf: 'flex-start' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: '12px 20px', borderBottom: `1px solid ${tokens.line.default}` }}>
          <ButtonGroup variant="contained" disabled={isRunning || (RUN_KINDS.every((k) => kindBlocked(k) !== null))} disableElevation>
            <Button
              disabled={mainBlocked !== null}
              title={mainBlocked ?? undefined}
              onClick={() => setConfirmKind(mainKind)}
              startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isRunning
                ? `${adminRun?.status === 'queued' && live.status !== 'running' ? 'Queued' : 'Running'} ${runningKindLabel}…`
                : `Run ${syncRunKindLabel(mainKind).toLowerCase()}`}
            </Button>
            <Button aria-label="Choose a sync type" onClick={(e) => setMenuAnchor(e.currentTarget)} size="small">
              <ArrowDropDown />
            </Button>
          </ButtonGroup>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {RUN_KINDS.map((k) => (
              <MenuItem
                key={k}
                disabled={kindBlocked(k) !== null}
                onClick={() => {
                  setMenuAnchor(null);
                  setConfirmKind(k);
                }}
                sx={{ display: 'block', maxWidth: 340, whiteSpace: 'normal' }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip label={syncRunKindChipLabel(k)} tone={syncRunKindTone(k)} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{syncRunKindLabel(k)}</Typography>
                </Box>
                <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.5 }}>
                  {kindBlocked(k) ?? syncRunKindHint(k)}
                </Typography>
              </MenuItem>
            ))}
          </Menu>

          {scheduled && (
            <Button
              variant="outlined"
              color="inherit"
              disabled={pause.isPending}
              startIcon={live.schedule.paused ? <PlayCircleOutline /> : <PauseCircleOutline />}
              onClick={() => pause.mutate(!live.schedule.paused)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {live.schedule.paused ? 'Resume schedule' : 'Pause schedule'}
            </Button>
          )}
          {live.storefrontUrl && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<OpenInNew />}
              component="a"
              href={live.storefrontUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Open store
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            disabled={!canImpersonate}
            onClick={() => {
              startImpersonationWithOrg({
                id: live.organizationId as string,
                name: live.organizationName || live.name,
                type: 'store',
                status: 'active',
              });
              navigate('/app/dashboard');
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Impersonate
          </Button>
        </Box>
        {(blocked || RUN_KINDS.every((k) => kindBlocked(k) !== null)) && (
          <Box sx={{ px: '20px', pt: 1.5 }}>
            <InlineMessage severity="info">{blocked ?? kindBlocked('pricing')}</InlineMessage>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: tokens.surface.subtle }}>
          <Box sx={{ p: '18px 20px 28px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(auto-fit, minmax(150px, 1fr))' },
                  gap: '1px',
                  bgcolor: tokens.line.default,
                  border: `1px solid ${tokens.line.default}`,
                  borderRadius: `${tokens.radius.control}px`,
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                {facts.map((f) => (
                  <Box key={f.label} sx={{ bgcolor: tokens.surface.card, p: '10px 12px' }}>
                    <Typography sx={{ ...sectionLabelSx, fontSize: 10.5, color: tokens.ink.faint }}>{f.label}</Typography>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        color: f.tone ? tokens.signal[f.tone].ink : tokens.ink.primary,
                      }}
                    >
                      {f.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {note && (
                <Box sx={{ flexShrink: 0, bgcolor: tokens.surface.card, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px`, p: '10px 12px' }}>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt }}>{note}</Typography>
                </Box>
              )}

              {live.lastRun && (
                <Box sx={{ flexShrink: 0 }}>
                  <Typography sx={{ ...sectionLabelSx, color: tokens.ink.faint, mb: 0.75 }}>Last run</Typography>
                  <Box sx={{ bgcolor: tokens.surface.card, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px`, p: '12px 14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <StatusChip label={syncRunKindLabel(lastKind)} tone={syncRunKindTone(lastKind)} />
                      <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
                        {formatWhen(live.lastRun.at)}
                        {live.lastRun.durationMs !== null ? ` · ${formatDuration(live.lastRun.durationMs)}` : ''} · {formatAgo(live.lastRun.at)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt, mt: 1 }}>{syncRunKindHint(lastKind)}</Typography>
                    {sub && <Typography sx={{ fontSize: 12, color: tokens.ink.muted, mt: 0.5 }}>{sub}</Typography>}
                  </Box>
                </Box>
              )}

              {detailQuery.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              {detailQuery.isError && (
                <InlineMessage severity="error">
                  Could not load run details for this store.{' '}
                  <Button size="small" onClick={() => detailQuery.refetch()} sx={{ textTransform: 'none' }}>
                    Try again
                  </Button>
                </InlineMessage>
              )}

              {detail && detail.errors.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ ...sectionLabelSx, color: tokens.signal.problem.ink }}>
                      Errors · {detail.errors.reduce((n, e) => n + e.count, 0)}
                    </Typography>
                    <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={copyErrors} sx={{ textTransform: 'none' }}>
                      Copy error
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {detail.errors.map((e) => (
                      <Box
                        key={e.code}
                        sx={{ bgcolor: tokens.surface.card, border: `1px solid ${tokens.signal.problem.border}`, borderRadius: `${tokens.radius.control}px`, p: '12px 14px' }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.problem.ink }}>
                          {e.title}
                          {e.count > 1 ? ` (${e.count})` : ''}
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: tokens.ink.bodyAlt, mt: 0.5 }}>{e.fix}</Typography>
                        <Box
                          component="pre"
                          sx={{
                            m: 0,
                            mt: 1,
                            p: '8px 10px',
                            bgcolor: tokens.surface.sunken,
                            borderRadius: `${tokens.radius.small}px`,
                            fontFamily: tokens.font.mono,
                            fontSize: 11.5,
                            whiteSpace: 'pre-wrap',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {e.raw}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {detail && detail.warnings.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <Typography sx={{ ...sectionLabelSx, color: tokens.signal.caution.ink, mb: 0.75 }}>
                    Warnings · {detail.warnings.reduce((n, w) => n + w.count, 0)}
                  </Typography>
                  <Box sx={{ bgcolor: tokens.surface.card, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px` }}>
                    {detail.warnings.map((w, i) => (
                      <Box
                        key={w.code}
                        sx={{ display: 'flex', gap: 1.25, p: '10px 14px', borderTop: i ? `1px solid ${tokens.line.hairline}` : 'none' }}
                      >
                        <Box
                          sx={{
                            alignSelf: 'flex-start',
                            minWidth: 26,
                            textAlign: 'center',
                            fontSize: 11.5,
                            fontWeight: 700,
                            borderRadius: `${tokens.radius.chip}px`,
                            px: 0.75,
                            py: '1px',
                            bgcolor: tokens.signal.caution.tint,
                            color: tokens.signal.caution.ink,
                          }}
                        >
                          {w.count}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{w.title}</Typography>
                          {w.samples.length > 0 && (
                            <Typography noWrap sx={{ fontSize: 12, color: tokens.ink.muted }}>
                              {w.samples.join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {detail && (
                <Box sx={{ flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ ...sectionLabelSx, color: tokens.ink.faint }}>Run history · last {Math.max(detail.runs.length, 10)}</Typography>
                    <Button
                      size="small"
                      startIcon={<Download fontSize="small" />}
                      disabled={detail.runs.length === 0}
                      onClick={exportCsv}
                      sx={{ textTransform: 'none' }}
                    >
                      Export CSV
                    </Button>
                  </Box>
                  <Box sx={{ bgcolor: tokens.surface.card, border: `1px solid ${tokens.line.default}`, borderRadius: `${tokens.radius.control}px`, overflowX: 'auto' }}>
                    {detail.runs.length === 0 ? (
                      <Typography sx={{ p: '12px 14px', fontSize: 12.5, color: tokens.ink.muted }}>
                        {detail.hasUnsummarizedRuns ? 'Older runs have no stored summary.' : 'No runs yet.'}
                      </Typography>
                    ) : (
                      <Box sx={{ minWidth: 420 }}>
                        {detail.runs.map((r, i) => (
                          <Box
                            key={r.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '110px 78px 1fr 64px 92px' },
                              gap: 1,
                              alignItems: 'center',
                              p: '9px 14px',
                              borderTop: i ? `1px solid ${tokens.line.hairline}` : 'none',
                              fontSize: 12.5,
                            }}
                          >
                            <span>{formatWhen(r.at)}</span>
                            <StatusChip label={syncRunKindChipLabel(r.kind)} tone={syncRunKindTone(r.kind)} />
                            <span>
                              {r.updated === null ? '—' : `${r.updated.toLocaleString()} ${syncRunUpdatedNoun(r.kind)}`}
                            </span>
                            <span>{formatDuration(r.durationMs) || '—'}</span>
                            <StatusChip label={RESULT_TEXT[r.result]} tone={RESULT_TONE[r.result]} dot />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <ConfirmDialog
        open={confirmKind !== null}
        title={confirmKind ? `Run ${syncRunKindLabel(confirmKind).toLowerCase()} for ${live.organizationName || live.name}?` : ''}
        message={
          confirmKind
            ? `${syncRunKindHint(confirmKind)} This writes to the store's ${platformLabel(live.platform)} account as a normal manual run, and it appears in the store's own sync history.${
                runsInBackground
                  ? ` ${platformLabel(live.platform)} runs in the background for up to ${RUN_BUDGET_MINUTES} minutes; a longer run stops between products and the next sync continues from there. You can close this drawer; the result shows in the run history.`
                  : ''
              }`
            : ''
        }
        confirmLabel={confirmKind ? `Run ${syncRunKindLabel(confirmKind).toLowerCase()}` : 'Run'}
        loading={run.isPending}
        onClose={() => setConfirmKind(null)}
        onConfirm={() => {
          if (!confirmKind) return;
          run.mutate(confirmKind, { onSettled: () => setConfirmKind(null) });
        }}
      />
    </>
  );
};
